import { useState, useRef, useCallback, useEffect } from "react";
import { isAddress } from "viem";
import { useI18n, LanguageSwitcher } from "../i18n";
import { createItem, getPresignedUploadUrl, uploadToR2, getActiveCoupon } from "../api/client";
import { ErrorCodes } from "../api/types";
import type { ActiveCouponResponse } from "../api/types";
import Link from "next/link";
import { useTermsContext } from "@/contexts/terms";
import { Turnstile, resetTurnstile, isTurnstileConfigured } from "./Turnstile";
import { useAuthContext } from "../providers/AuthProvider";
import { GoogleSignInButton } from "./GoogleSignInButton";

type SellerStatus =
  | "editing"
  | "compressing"
  | "uploading"
  | "creating"
  | "success"
  | "error";

interface FormData {
  title: string;
  description: string;
  files: File[];
  price: string;
  payoutAddress: string;
  validMinutes: number;
}

const MOCK_USDC_TO_TWD = 32;
const MAX_VALID_MINUTES = 1440; // 24 小時
const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB
const MAX_PRICE = 200;
const MAX_FILE_COUNT = 20;

export function SellerPage() {
  const { t, locale } = useI18n();
  const terms = useTermsContext();
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const [status, setStatus] = useState<SellerStatus>("editing");
  const [generatedLink, setGeneratedLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [fileErrors, setFileErrors] = useState<string | null>(null);
  const [compressProgress, setCompressProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [activeCoupon, setActiveCoupon] = useState<ActiveCouponResponse | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Turnstile 回調
  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  // 頁面載入時查詢 coupon 狀態
  const fetchActiveCoupon = useCallback(async () => {
    setCouponLoading(true);
    try {
      const res = await getActiveCoupon();
      if (res.code === 0) {
        setActiveCoupon(res.data);
      }
    } catch {
      // silently fail
    } finally {
      setCouponLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchActiveCoupon();
    }
  }, [isAuthenticated, fetchActiveCoupon]);

  const [form, setForm] = useState<FormData>({
    title: "",
    description: "",
    files: [],
    price: "",
    payoutAddress: "",
    validMinutes: 60,
  });

  const updateForm = (updates: Partial<FormData>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  // Calculate totals for validation
  const totalSize = form.files.reduce((sum, f) => sum + f.size, 0);
  const remainingSize = MAX_TOTAL_SIZE - totalSize;
  const remainingCount = MAX_FILE_COUNT - form.files.length;

  // 驗證以太坊地址
  const isValidAddress = form.payoutAddress.trim() === "" || isAddress(form.payoutAddress.trim());

  // 檢查 Turnstile 是否通過（如果有配置的話）
  const turnstileValid = !isTurnstileConfigured() || !!turnstileToken;

  const isValid =
    form.title.trim() &&
    form.price.trim() &&
    form.payoutAddress.trim() &&
    isValidAddress &&
    form.files.length > 0 &&
    form.files.length <= MAX_FILE_COUNT &&
    totalSize <= MAX_TOTAL_SIZE &&
    turnstileValid;

  // 地址輸入時驗證
  const handleAddressChange = (value: string) => {
    updateForm({ payoutAddress: value });
    if (value.trim() && !isAddress(value.trim())) {
      setAddressError(t.seller.invalidAddressError || "Invalid Ethereum address");
    } else {
      setAddressError(null);
    }
  };

  const fiatEstimate = form.price && locale === "zh"
    ? `${t.seller.estimated} NT$${Math.round(parseFloat(form.price) * MOCK_USDC_TO_TWD)}`
    : "";

  // Validate and add files
  const validateAndAddFiles = (newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const currentFiles = form.files;

    // Check file count
    if (currentFiles.length + fileArray.length > MAX_FILE_COUNT) {
      setFileErrors(t.seller.maxFilesError);
      return;
    }

    // Check total size
    const newTotalSize =
      currentFiles.reduce((s, f) => s + f.size, 0) +
      fileArray.reduce((s, f) => s + f.size, 0);

    if (newTotalSize > MAX_TOTAL_SIZE) {
      setFileErrors(t.seller.maxSizeError);
      return;
    }

    // Clear errors and add files
    setFileErrors(null);
    updateForm({ files: [...currentFiles, ...fileArray] });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      validateAndAddFiles(files);
    }
    // Reset input value to allow selecting same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = form.files.filter((_, i) => i !== index);
    updateForm({ files: newFiles });
    setFileErrors(null);
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndAddFiles(files);
    }
  };

  // 壓縮檔案成 ZIP
  const compressFiles = async (files: File[]): Promise<Blob> => {
    const { default: JSZip } = await import("jszip");
    const zip = new JSZip();

    for (const file of files) {
      zip.file(file.name, file);
    }

    const blob = await zip.generateAsync(
      { type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } },
      (metadata) => {
        setCompressProgress(Math.round(metadata.percent));
      }
    );

    return blob;
  };

  const handleSubmit = async () => {
    if (!isValid || form.files.length === 0) return;

    // 驗證價格最低 1 USDC
    const price = parseFloat(form.price);
    if (isNaN(price) || price < 1) {
      setErrorMessage(t.seller.priceMinError || "Price must be at least 1 USDC");
      setStatus("error");
      return;
    }

    // 驗證價格上限 200 USDC
    if (price > MAX_PRICE) {
      setErrorMessage(t.seller.priceMaxError || "Price cannot exceed 200 USDC");
      setStatus("error");
      return;
    }

    // 驗證小數位數不超過 2 位
    const decimalPart = form.price.split(".")[1];
    if (decimalPart && decimalPart.length > 2) {
      setErrorMessage(t.seller.priceDecimalError || "Price cannot have more than 2 decimal places");
      setStatus("error");
      return;
    }

    // 驗證時間範圍（30 秒 ~ 24 小時）
    const durationMs = form.validMinutes * 60 * 1000;
    if (durationMs < 30 * 1000) {
      setErrorMessage(t.seller.validMinutesMinError || "有效時間必須至少 30 秒");
      setStatus("error");
      return;
    }
    if (durationMs > 24 * 60 * 60 * 1000) {
      setErrorMessage(t.seller.validMinutesMaxError || "有效時間不能超過 24 小時");
      setStatus("error");
      return;
    }

    // 條款檢查（會呼叫 API 確認後端狀態）
    // 賣家模式：checkAsUser=true 讓 API 用 JWT user_id 查詢（而非 wallet_address）
    // 這樣登入後的條款同意才會正確綁定到 user_id
    if (form.payoutAddress) {
      // 檢查 TOS（用 user mode 查詢，但 accept 時仍使用 payoutAddress）
      const tosAccepted = await terms.checkAndPrompt(form.payoutAddress, "TOS", { checkAsUser: true });
      if (!tosAccepted) return;

      // 檢查 SELLER_DISCLAIMER
      const sellerAccepted = await terms.checkAndPrompt(form.payoutAddress, "SELLER_DISCLAIMER", { checkAsUser: true });
      if (!sellerAccepted) return;
    }

    setErrorMessage("");
    setCompressProgress(0);
    setUploadProgress(0);

    try {
      // Step 1: 壓縮檔案
      setStatus("compressing");
      const zipBlob = await compressFiles(form.files);

      // 檢查壓縮後大小是否超過限制
      if (zipBlob.size > MAX_TOTAL_SIZE) {
        setErrorMessage(t.seller.zipTooLarge || "Compressed file exceeds 20 MB limit");
        setStatus("error");
        return;
      }

      // Step 2: 取得 Presigned Upload URL
      setStatus("uploading");
      const presignResponse = await getPresignedUploadUrl({
        sellerAddress: form.payoutAddress,
        fileSize: zipBlob.size,
      });

      if (presignResponse.code !== 0) {
        // 處理認證錯誤
        if (
          presignResponse.code === ErrorCodes.MISSING_AUTH ||
          presignResponse.code === ErrorCodes.INVALID_AUTH ||
          presignResponse.code === ErrorCodes.AUTH_TOKEN_EXPIRED
        ) {
          setErrorMessage(t.auth?.sessionExpired || "Session expired, please sign in again");
          setStatus("error");
          return;
        }
        // 處理特定錯誤碼
        if (presignResponse.code === ErrorCodes.RATE_LIMIT_EXCEEDED) {
          setErrorMessage(t.seller.rateLimitExceeded || "Too many requests, please try again later");
        } else {
          setErrorMessage(presignResponse.msg || t.seller.errorMessage);
        }
        setStatus("error");
        return;
      }

      const { upload_url, session_id } = presignResponse.data;

      // Step 3: 上傳到 R2
      await uploadToR2(upload_url, zipBlob, (progress) => {
        setUploadProgress(progress);
      });

      // Step 4: 建立商品
      setStatus("creating");
      const validFrom = new Date();
      const validUntil = new Date(Date.now() + durationMs);

      const response = await createItem({
        sessionId: session_id,
        title: form.title,
        description: form.description,
        price: form.price,
        sellerAddress: form.payoutAddress,
        validFrom,
        validUntil,
        turnstileToken: turnstileToken || undefined,
      });

      if (response.code === 0) {
        // 使用當前 domain 組合 URL，而非後端返回的固定 URL
        // 這樣在 preview 環境也能正常運作
        const currentOrigin = window.location.origin;
        setGeneratedLink(`${currentOrigin}/u/${response.data.id}`);
        setStatus("success");
      } else {
        // 處理特定錯誤碼
        if (
          response.code === ErrorCodes.UPLOAD_SESSION_NOT_FOUND ||
          response.code === ErrorCodes.UPLOAD_SESSION_EXPIRED
        ) {
          setErrorMessage(t.seller.sessionExpired || "Upload session expired, please try again");
        } else if (response.code === ErrorCodes.RATE_LIMIT_EXCEEDED) {
          setErrorMessage(t.seller.rateLimitExceeded || "Too many requests, please try again later");
        } else if (response.code === ErrorCodes.FILE_NOT_FOUND_R2) {
          setErrorMessage(t.seller.errorMessage);
        } else if (response.code === ErrorCodes.TERMS_NOT_ACCEPTED) {
          // 條款未同意 - 清除快取，下次會重新檢查
          terms.clearCache();
          setErrorMessage(t.seller.errorMessage);
        } else if (response.code === ErrorCodes.PRICE_EXCEEDS_MAX) {
          setErrorMessage(t.seller.priceMaxError || "Price cannot exceed 200 USDC");
        } else if (
          response.code === ErrorCodes.TURNSTILE_VERIFICATION_FAILED ||
          response.code === ErrorCodes.TURNSTILE_TOKEN_MISSING
        ) {
          setErrorMessage(t.seller.turnstileError || "Verification failed, please try again");
        } else {
          setErrorMessage(response.msg || t.seller.errorMessage);
        }
        setStatus("error");
        // 重置 Turnstile 以便重試
        resetTurnstile();
        setTurnstileToken(null);
      }
    } catch {
      setErrorMessage(t.seller.errorMessage);
      setStatus("error");
      // 重置 Turnstile 以便重試
      resetTurnstile();
      setTurnstileToken(null);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement("input");
      input.value = generatedLink;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCreateAnother = () => {
    setForm({
      title: "",
      description: "",
      files: [],
      price: "",
      payoutAddress: "",
      validMinutes: 60,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setGeneratedLink("");
    setStatus("editing");
    setCopied(false);
    setErrorMessage("");
    setFileErrors(null);
    setCompressProgress(0);
    setUploadProgress(0);
    // 不需要呼叫 resetTurnstile()，因為從 success 狀態回到 editing 時
    // Turnstile 組件會被重新創建
    setTurnstileToken(null);
  };

  const handlePreview = () => {
    window.open(`${generatedLink}?preview=true`, "_blank");
  };

  // Show login prompt if not authenticated
  if (!isAuthenticated && !authLoading) {
    return (
      <div className="seller-page">
        <div className="page-language-switcher">
          <LanguageSwitcher />
        </div>
        <header className="seller-header">
          <h1 className="seller-title">{t.seller.title}</h1>
          <p className="seller-subtitle">{t.seller.subtitle}</p>
        </header>

        <main className="seller-main">
          <div className="login-prompt">
            <div className="login-prompt-icon">
              <img src="/logo.png" alt="Govilo PayLink logo" className="login-prompt-logo" />
            </div>
            <h2 className="login-prompt-title">{t.auth?.loginRequired || "Sign in to continue"}</h2>
            <p className="login-prompt-desc">
              {t.auth?.loginRequiredDesc || "Sign in with your Google account to create unlock links."}
            </p>
            <div className="login-prompt-button">
              <GoogleSignInButton />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="seller-page">
        <div className="page-language-switcher">
          <LanguageSwitcher />
        </div>
        <header className="seller-header">
          <h1 className="seller-title">{t.seller.title}</h1>
        </header>
        <main className="seller-main">
          <div className="loading-state">
            <span className="btn-spinner" />
            <p>{t.common?.loading || "Loading..."}</p>
          </div>
        </main>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="seller-page">
        <div className="page-language-switcher">
          <LanguageSwitcher />
        </div>
        <header className="seller-header">
          <h1 className="seller-title">{t.seller.title}</h1>
        </header>

        <main className="seller-main">
          <div className="success-state">
            <div className="success-icon">
              <CheckCircleIcon />
            </div>
            <h2 className="success-title">{t.seller.successTitle}</h2>

            <div className="generated-link-box">
              <input
                type="text"
                className="generated-link-input"
                value={generatedLink}
                readOnly
              />
            </div>

            <button className="copy-btn" onClick={handleCopyLink}>
              {copied ? (
                <>
                  <CheckIcon /> {t.seller.copied}
                </>
              ) : (
                <>
                  <CopyIcon /> {t.seller.copyLink}
                </>
              )}
            </button>

            <div className="success-actions">
              <button className="secondary-btn" onClick={handleCreateAnother}>
                {t.seller.createAnother}
              </button>
              <button className="secondary-btn" onClick={handlePreview}>
                {t.seller.previewBuyer}
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="seller-page">
      <div className="page-language-switcher">
        <LanguageSwitcher />
      </div>
      <header className="seller-header">
        <h1 className="seller-title">{t.seller.title}</h1>
        <p className="seller-subtitle">{t.seller.subtitle}</p>
      </header>

      <main className="seller-main">
        {/* Section 1: Content Information */}
        <section className="form-section">
          <h2 className="section-title">{t.seller.contentInfo}</h2>

          <div className="form-group">
            <label className="form-label">
              {t.seller.fieldTitle}{" "}
              <span className="required">{t.seller.required}</span>
            </label>
            <input
              type="text"
              className="form-input"
              placeholder={t.seller.fieldTitlePlaceholder}
              value={form.title}
              onChange={(e) => updateForm({ title: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t.seller.fieldDescription}</label>
            <textarea
              className="form-textarea"
              placeholder={t.seller.fieldDescriptionPlaceholder}
              rows={3}
              value={form.description}
              onChange={(e) => updateForm({ description: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              {t.seller.fieldUploadFile}{" "}
              <span className="required">{t.seller.required}</span>
            </label>

            {/* Drag & Drop Zone */}
            <div
              className={`file-dropzone ${isDragging ? "file-dropzone-active" : ""}`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="file-input"
                onChange={handleFileChange}
                id="file-upload"
                multiple
              />
              <label htmlFor="file-upload" className="file-dropzone-label">
                <UploadIcon />
                <span className="dropzone-text">{t.seller.dropzoneText}</span>
                <span className="dropzone-hint">{t.seller.dropzoneHint}</span>
              </label>
            </div>

            {/* File List */}
            {form.files.length > 0 && (
              <div className="file-list">
                <div className="file-list-header">
                  <span>
                    {t.seller.fileCount.replace("{count}", String(form.files.length))}
                  </span>
                  <span className="file-list-size">
                    {formatFileSize(totalSize)} / 20 MB
                  </span>
                </div>

                {form.files.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="file-list-item">
                    <FileIcon />
                    <span className="file-list-name">{file.name}</span>
                    <span className="file-list-item-size">
                      {formatFileSize(file.size)}
                    </span>
                    <button
                      type="button"
                      className="file-remove-btn"
                      onClick={() => handleRemoveFile(index)}
                      aria-label={t.seller.removeFile}
                    >
                      <XIcon />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Capacity Progress Bar */}
            {form.files.length > 0 && (
              <div className="file-capacity">
                <div className="capacity-bar">
                  <div
                    className={`capacity-fill ${
                      totalSize / MAX_TOTAL_SIZE > 0.9
                        ? "capacity-danger"
                        : totalSize / MAX_TOTAL_SIZE > 0.7
                          ? "capacity-warning"
                          : ""
                    }`}
                    style={{ width: `${(totalSize / MAX_TOTAL_SIZE) * 100}%` }}
                  />
                </div>
                <div className="capacity-info">
                  <span>
                    {t.seller.remaining}: {formatFileSize(remainingSize)}
                  </span>
                  <span>
                    {remainingCount} {t.seller.filesRemaining}
                  </span>
                </div>
              </div>
            )}

            {/* Error Display */}
            {fileErrors && <div className="file-error">{fileErrors}</div>}

            <p className="form-hint">{t.seller.multiFileHint}</p>
          </div>
        </section>

        {/* Section 2: Pricing */}
        <section className="form-section">
          <h2 className="section-title">{t.seller.pricing}</h2>

          <div className="form-group">
            <label className="form-label">
              {t.seller.fieldPrice}{" "}
              <span className="required">{t.seller.required}</span>
            </label>
            <div className="price-input-wrapper">
              <input
                type="number"
                className="form-input price-input"
                placeholder={t.seller.pricePlaceholder}
                min="1"
                max="200"
                step="0.01"
                value={form.price}
                onChange={(e) => {
                  let val = e.target.value;
                  // 限制小數點後最多 2 位
                  const parts = val.split(".");
                  if (parts[1] && parts[1].length > 2) {
                    val = parts[0] + "." + parts[1].slice(0, 2);
                  }
                  updateForm({ price: val });
                }}
                onBlur={(e) => {
                  const num = parseFloat(form.price);
                  if (!isNaN(num) && form.price !== "") {
                    // 失焦時，如果小於 1 就改成 1，超過 200 就改成 200
                    if (num < 1) {
                      updateForm({ price: "1" });
                    } else if (num > MAX_PRICE) {
                      updateForm({ price: String(MAX_PRICE) });
                    }
                  }
                }}
              />
              <span className="price-unit">{t.seller.priceUnit}</span>
            </div>
            {fiatEstimate && <p className="fiat-estimate">{fiatEstimate}</p>}
          </div>
        </section>

        {/* Section 3: Payout */}
        <section className="form-section">
          <h2 className="section-title">{t.seller.payout}</h2>

          <div className="form-group">
            <label className="form-label">
              {t.seller.fieldPayoutAddress}{" "}
              <span className="required">{t.seller.required}</span>
            </label>
            <input
              type="text"
              name="payout-address"
              autoComplete="on"
              className={`form-input font-mono ${addressError ? "input-error" : ""}`}
              placeholder={t.seller.payoutAddressPlaceholder}
              value={form.payoutAddress}
              onChange={(e) => handleAddressChange(e.target.value)}
            />
            {addressError ? (
              <p className="form-error">{addressError}</p>
            ) : (
              <p className="form-hint">{t.seller.payoutAddressHint}</p>
            )}
          </div>
        </section>

        {/* Section 4: Advanced Settings */}
        <section className="form-section">
          <h2 className="section-title">{t.seller.advanced}</h2>

          <div className="form-group">
            <label className="form-label">{t.seller.validDuration}</label>
            <div className="duration-input-wrapper">
              <input
                type="number"
                className="form-input"
                value={form.validMinutes}
                onChange={(e) => {
                  let val = Number(e.target.value) || 1;
                  if (val > MAX_VALID_MINUTES) val = MAX_VALID_MINUTES;
                  if (val < 1) val = 1;
                  updateForm({ validMinutes: val });
                }}
                min={1}
                max={MAX_VALID_MINUTES}
              />
              <span className="duration-unit">{t.seller.minutes}</span>
              {form.validMinutes >= 60 && (
                <span className="duration-hours">
                  = {(form.validMinutes / 60).toFixed(1)} {locale === "zh" ? "小時" : "hours"}
                </span>
              )}
            </div>
            <p className="form-hint">{t.seller.validDurationHint}</p>
          </div>
        </section>

        {/* Turnstile Bot Protection */}
        <div className="turnstile-container">
          <Turnstile
            onVerify={handleTurnstileVerify}
            onExpire={handleTurnstileExpire}
            theme="auto"
          />
        </div>

        {/* Error State */}
        {status === "error" && (
          <div className="form-error">{errorMessage || t.seller.errorMessage}</div>
        )}

        {/* Action Row: Generate Button + Fee Badge */}
        <div className="action-row">
          <button
            className="generate-btn"
            disabled={
              !isValid ||
              status === "compressing" ||
              status === "uploading" ||
              status === "creating"
            }
            onClick={handleSubmit}
          >
            {status === "compressing" ? (
              <>
                <span className="btn-spinner" />
                {t.seller.compressingProgress
                  ? t.seller.compressingProgress.replace("{progress}", String(compressProgress))
                  : `Compressing: ${compressProgress}%`}
              </>
            ) : status === "uploading" ? (
              <>
                <span className="btn-spinner" />
                {t.seller.uploadingProgress
                  ? t.seller.uploadingProgress.replace("{progress}", String(uploadProgress))
                  : `Uploading: ${uploadProgress}%`}
              </>
            ) : status === "creating" ? (
              <>
                <span className="btn-spinner" />
                {t.seller.creatingItem || "Creating item..."}
              </>
            ) : (
              t.seller.generateBtn
            )}
          </button>

          {!couponLoading && activeCoupon && (
            <Link
              href="/dashboard/settings"
              className={`fee-badge ${activeCoupon.has_active_coupon ? "fee-badge--promo" : ""}`}
              title={
                activeCoupon.has_active_coupon && activeCoupon.expires_at
                  ? t.seller.couponActiveInfo.replace("{date}", new Date(activeCoupon.expires_at).toLocaleDateString())
                  : t.seller.couponCurrentFee.replace("{fee}", String(activeCoupon.current_fee_percent))
              }
            >
              <span className="fee-badge__label">{locale === "zh" ? "費率" : "Fee"}</span>
              <span className="fee-badge__value">{activeCoupon.current_fee_percent}%</span>
              {activeCoupon.has_active_coupon && (
                <span className="fee-badge__dot" />
              )}
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Icons
function CheckCircleIcon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="9 12 12 15 16 10" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}


